/**
 * ActiveCombatPopup — centered modal showing the live attack/defend exchange
 * currently awaiting reaction-window resolution.
 *
 * Lifecycle (mirrors original-zip behavior):
 *   1. Aggressor plays an attack → popup opens with attack card visible,
 *      block side shows "Awaiting block…" prompt.
 *   2. Defender chooses a defend (or armor / equip / sig block) → popup
 *      now shows both cards. ReactionChain begins; aggressor has priority.
 *   3. Each player can play a legal reaction (counter / utility / etc.) OR
 *      hit Pass / Resolve. Playing a reaction resets consecutivePasses to 0.
 *      Hitting Pass increments consecutivePasses and flips priority.
 *   4. When both players Pass back-to-back (consecutivePasses ≥ 2), the
 *      exchange resolves (engine.resolveCombat).
 *
 * The popup is purely presentational — all state lives in GameState
 * (combatPopup + reactionChain). The parent wires the action callbacks.
 */
import type { Card } from "@/types/card";
import type { CombatSlot, GameState } from "@/types/game";
import { instanceValue, syntheticCardFor } from "@/game/playtestEngine";
import { Plus, Minus, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ActiveCombatPopupProps {
  game: GameState;
  cardMap: Map<string, Card>;
  selfNum: 1 | 2;
  /** Pass / Resolve. Both branches share one button per the original. */
  onPass: () => void;
  /** Defender's "Take damage instead of blocking" path. */
  onTakeDamage: () => void;
  /** Adjust power/block values (debug). */
  onAdjustValue: (
    n: 1 | 2,
    instanceId: string,
    cardDbId: string,
    mode: "attack" | "defend",
    delta: number,
  ) => void;
  /** Manually close (only available after the exchange resolves). */
  onDismiss?: () => void;
}

/** Find the most-recent unresolved combat slot — the "active" exchange. */
function findActiveSlot(slots: CombatSlot[]): CombatSlot | null {
  for (let i = slots.length - 1; i >= 0; i--) {
    if (!slots[i].resolved) return slots[i];
  }
  return null;
}

/** Pull keywords for a card on the attack-or-defend side, including buffs. */
function collectKeywords(
  card: Card | null | undefined,
  side: "attack" | "defend",
  player: { pendingBuffs: { sourceCardName: string; effect: { kind: string; power?: number; count?: number } }[] },
  slot: CombatSlot,
): string[] {
  if (!card) return [];
  const base = card.keywords ?? [];
  const sideSpecific =
    side === "attack" ? (card.attackKeywords ?? []) : (card.defendKeywords ?? []);
  const granted =
    side === "attack"
      ? (slot.attackGrantedKeywords ?? [])
      : (slot.blockGrantedKeywords ?? []);
  // Buffs from same card are already represented; skip self-buffs.
  const fromBuffs = (player.pendingBuffs ?? [])
    .filter((b) => b.sourceCardName !== card.name)
    .flatMap((b) => {
      switch (b.effect.kind) {
        case "gain-stun":
          return ["Stun"];
        case "gain-quick":
          return ["Quick"];
        case "gain-piercing":
          return ["Piercing"];
        case "gain-chain":
          return ["Chain"];
        case "gain-reflect":
          return [`Reflect ${b.effect.power ?? ""}`.trim()];
        case "gain-disarm":
          return [`Disarm ${b.effect.count ?? ""}`.trim()];
        default:
          return [];
      }
    });
  return Array.from(new Set([...base, ...sideSpecific, ...granted, ...fromBuffs]));
}

export function ActiveCombatPopup({
  game,
  cardMap,
  selfNum,
  onPass,
  onTakeDamage,
  onAdjustValue,
  onDismiss,
}: ActiveCombatPopupProps) {
  const slot = findActiveSlot(game.combatSlots ?? []);
  if (!slot) return null;

  const attackerNum = slot.attackerNum;
  const defenderNum: 1 | 2 = attackerNum === 1 ? 2 : 1;
  const attacker = game.players[attackerNum];
  const defender = game.players[defenderNum];

  // Synthetic Reflect attacks have no entry in cardMap; build a stub so the
  // popup renders the attack side normally. The stub carries the synthetic
  // power, an empty keyword set, and the display name "Reflect Attack".
  const attackCard = slot.synthetic
    ? syntheticCardFor(slot)
    : cardMap.get(slot.attackCard.cardId) ?? null;
  const blockCard = slot.blockCard ? cardMap.get(slot.blockCard.cardId) ?? null : null;

  const attackPower = attackCard
    ? instanceValue(attacker, slot.attackCard.id, "attack", attackCard)
    : 0;
  const blockValue =
    blockCard && slot.blockCard
      ? instanceValue(defender, slot.blockCard.id, "defend", blockCard)
      : 0;

  const isMyAttack = attackerNum === selfNum;
  const isMyBlock = defenderNum === selfNum;
  const myTurn = game.priority === selfNum;

  // Phase logic per spec:
  //   1. Attack played → "Awaiting Response" — defender owes a block or take-damage.
  //      No reaction window yet, no Pass button.
  //   2. Block attached → reactionChain opens. Aggressor passes first, then defender.
  //      Pass button visible. Label flips to Resolve when consecutivePasses ≥ 1.
  //   3. If anyone plays a card mid-window, consecutivePasses resets to 0
  //      → Resolve flips back to Pass.
  const subPhase = game.subPhase;
  const chain = game.reactionChain ?? null;
  const consecutivePasses = chain?.consecutivePasses ?? 0;

  const reactionOpen = chain != null && slot.blockCard != null;
  const awaitingResponse = !slot.blockCard && subPhase === "defender-pre-block";

  const willResolve = consecutivePasses >= 1;
  const passLabel = willResolve ? "✓ Resolve" : "Pass →";

  const mustTakeDamage =
    isMyBlock &&
    !slot.blockCard &&
    !slot.resolved &&
    subPhase === "defender-pre-block" &&
    game.priority === defenderNum;

  // Border pulse cue when it's my turn.
  const myCue = myTurn
    ? "ring-2 ring-amber-soft/60 shadow-[0_0_60px_rgba(245,158,11,0.25)]"
    : "";

  // Inline overlay: positioned absolutely over the CombatDock parent.
  // Fully opaque so it completely covers the dock; sized to the dock exactly.
  // Border width MUST match the CombatDock (1px / `border`) so the popup is
  // pixel-aligned to the dock's edge — a 2px border was making the popup look
  // slightly larger than the underlying combat zone box.
  return (
    <div className="absolute inset-0 z-30 flex items-stretch">
      <div
        className={cn(
          "relative flex h-full w-full flex-col rounded-md border border-violet-500/60 bg-[#0e1014] p-2 shadow-xl shadow-violet-900/30",
          myCue,
        )}
      >
        {/* Header — phase pill is the only thing here, kept tight */}
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-violet-300">
              ⚔ Active Combat
            </span>
            {awaitingResponse ? (
              <span className="animate-pulse rounded bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-300">
                {defender.name} must respond
              </span>
            ) : reactionOpen ? (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider",
                  willResolve
                    ? "bg-emerald-500/20 text-emerald-200"
                    : "bg-violet-500/20 text-violet-200",
                )}
              >
                {willResolve ? "Resolves on next pass" : "Reaction Window"} — {(game.priority === 1 ? game.players[1] : game.players[2]).name}
              </span>
            ) : null}
          </div>
          {onDismiss && slot.resolved && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded p-0.5 text-[#94a3b8] hover:bg-[#22262f] hover:text-white"
              aria-label="Close"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Card pair */}
        <div className="grid flex-1 grid-cols-[1fr_auto_1fr] gap-2 sm:gap-4">
          <CombatSide
            label={`⚔ ${attacker.name}`}
            side="attack"
            card={attackCard}
            value={attackPower}
            keywords={collectKeywords(attackCard, "attack", attacker, slot)}
            canAdjust={isMyAttack}
            onAdjust={(d) =>
              attackCard &&
              onAdjustValue(attackerNum, slot.attackCard.id, attackCard.id, "attack", d)
            }
          />

          <div className="flex items-center justify-center text-2xl font-bold text-[#64748b]">
            vs
          </div>

          {blockCard ? (
            <CombatSide
              label={`🛡 ${defender.name}`}
              side="defend"
              card={blockCard}
              value={blockValue}
              keywords={collectKeywords(blockCard, "defend", defender, slot)}
              canAdjust={isMyBlock}
              onAdjust={(d) =>
                blockCard &&
                slot.blockCard &&
                onAdjustValue(defenderNum, slot.blockCard.id, blockCard.id, "defend", d)
              }
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#2a2f3a] bg-[#13151a] px-3 py-6 text-center">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                Awaiting block…
              </span>
              <span className="text-[10px] text-[#475569]">
                {defender.name} must defend or take damage
              </span>
            </div>
          )}
        </div>

        {/* Footer hint — just text, no buttons here. Buttons float bottom-right
            (outside the card grid) so they never cover combat cards. */}
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="text-[9px] text-[#64748b]">
            {reactionOpen
              ? willResolve
                ? "Both sides passed — click Resolve to end the exchange."
                : "Play a reaction or pass to flip priority."
              : awaitingResponse
                ? "Block, take damage, or play a counter."
                : "…"}
          </p>
        </div>

        {/* Floating action bar — absolute, bottom-right of the popup so it does
            not cover the attack/defend cards in the center. Compact buttons. */}
        {(mustTakeDamage || (reactionOpen && myTurn)) && (
          <div className="absolute bottom-1 right-1 flex items-center gap-1.5">
            {mustTakeDamage && (
              <button
                type="button"
                onClick={onTakeDamage}
                className="rounded border border-red-500/60 bg-red-600/85 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-md hover:bg-red-500"
              >
                Take {attackPower}
              </button>
            )}
            {reactionOpen && myTurn && (
              <button
                type="button"
                onClick={onPass}
                className={cn(
                  "rounded border px-2.5 py-1 text-[10px] font-black uppercase tracking-wide shadow-md",
                  willResolve
                    ? "border-emerald-500/70 bg-emerald-600/90 text-white shadow-emerald-500/30 hover:bg-emerald-500"
                    : "border-amber-500/70 bg-amber-500/30 text-amber-100 hover:bg-amber-500/50",
                )}
              >
                {passLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Card side (attack or defend) ──────────────────────────────────────────

interface CombatSideProps {
  label: string;
  side: "attack" | "defend";
  card: Card | null;
  value: number;
  keywords: string[];
  canAdjust: boolean;
  onAdjust: (delta: number) => void;
}

function CombatSide({
  label,
  side,
  card,
  value,
  keywords,
  canAdjust,
  onAdjust,
}: CombatSideProps) {
  const accent =
    side === "attack"
      ? "border-red-500/60 bg-red-500/5"
      : "border-blue-500/60 bg-blue-500/5";
  const big = side === "attack" ? "text-red-300" : "text-blue-300";

  const weight =
    card?.attackDefendFields?.weight ??
    card?.signatureMoveFields?.attackWeight ??
    "";
  const attackType =
    card?.attackDefendFields?.attackType ??
    card?.attackDefendFields?.defendType ??
    card?.signatureMoveFields?.attackType ??
    "";

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col items-center gap-1 rounded border-2 p-1 sm:p-1.5",
        accent,
      )}
    >
      <span className="text-[8px] font-semibold uppercase tracking-wider text-[#94a3b8] sm:text-[9px]">
        {label} — {side === "attack" ? "Attack" : "Defend"}
      </span>

      {/* Card visual with overlaid power/block — sized to fit the dock height */}
      <div className="relative h-16 w-12 overflow-hidden rounded-md border-2 border-[#2a2f3a] bg-[#13151a] sm:h-20 sm:w-16">
        {card?.imageDataUrl ? (
          <img
            src={card.imageDataUrl}
            alt={card.name}
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="text-4xl font-black text-white/10">
              {card?.cardType?.[0] ?? "?"}
            </span>
          </div>
        )}
        {/* Big power / block overlay */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              "text-2xl font-black drop-shadow-[0_0_8px_rgba(0,0,0,0.95)] sm:text-3xl",
              big,
            )}
          >
            {value}
          </span>
        </div>
        {/* Bottom name strip */}
        <div className="absolute inset-x-0 bottom-0 bg-black/80 px-1.5 py-1">
          <p className="line-clamp-2 text-[10px] font-bold leading-tight text-white">
            {card?.name ?? "?"}
          </p>
        </div>
      </div>

      {/* Pwr/Blk + adjust */}
      <div className="flex items-center gap-0.5">
        {canAdjust && (
          <button
            type="button"
            onClick={() => onAdjust(-1)}
            className="rounded border border-[#2a2f3a] bg-[#13151a] p-0.5 text-[#94a3b8] hover:bg-[#22262f]"
          >
            <Minus size={11} />
          </button>
        )}
        <span
          className={cn(
            "rounded px-2 py-0.5 font-mono text-[11px] font-bold",
            side === "attack" ? "bg-red-500/30 text-red-200" : "bg-blue-500/30 text-blue-200",
          )}
        >
          {side === "attack" ? "PWR" : "BLK"} {value}
        </span>
        {canAdjust && (
          <button
            type="button"
            onClick={() => onAdjust(1)}
            className="rounded border border-[#2a2f3a] bg-[#13151a] p-0.5 text-[#94a3b8] hover:bg-[#22262f]"
          >
            <Plus size={11} />
          </button>
        )}
      </div>

      {/* Pills: weight + attack type + keywords — collapsed into one line */}
      <div className="flex flex-wrap justify-center gap-0.5">
        {weight && (
          <span className="rounded bg-amber-500/15 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-amber-300">
            {weight}
          </span>
        )}
        {attackType && (
          <span className="rounded bg-blue-500/15 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-blue-300">
            {attackType}
          </span>
        )}
        {keywords.slice(0, 3).map((k) => (
          <span
            key={k}
            className="rounded bg-violet-500/15 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-violet-300"
          >
            {k}
          </span>
        ))}
        {keywords.length > 3 && (
          <span className="text-[8px] text-[#64748b]">+{keywords.length - 3}</span>
        )}
      </div>
    </div>
  );
}
